import React, { useEffect, useMemo, useRef, useState } from 'react';
import { translate } from '@fiverr-private/i18n-react';
import { GuaranteeIcon } from '@fiverr-private/icons';
import { Container, Stack } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { Text, Link } from '@fiverr-private/typography';
import { useEnteredView } from '@fiverr-private/hooks';
import { getContext } from '@fiverr-private/fiverr_context';
import { PLATFORM, bigQuery } from '../../../monitoring';
import { useFreelancerPageContext } from '../../FreelancerPageContext';
import { COPY_KEY, HREF, VIDEO_SRC } from './constants';

interface CustomerProtectionBannerProps extends Omit<React.ComponentProps<typeof Stack>, 'ref'> {}

export const CustomerProtectionBanner: React.FC<CustomerProtectionBannerProps> = (props) => {
    const observableRef = useRef<HTMLElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playCount = useRef(0);
    const [videoReady, setVideoReady] = useState(false);
    const [playTriggered, setPlayTriggered] = useState(false);

    const { seller } = useFreelancerPageContext();
    const { userGuid, isTouch, userId } = getContext();

    const isWebmSupported = useMemo(() => {
        if (typeof document === 'undefined') {
            return false;
        }

        const video = document.createElement('video');
        return video.canPlayType('video/webm;codecs=vp9') === 'probably';
    }, []);

    const playVideo = () => {
        playCount.current++;
        videoRef.current?.play();
    };

    const onVideoEnd = () => {
        if (playCount.current < 2) {
            playVideo();
        }
    };

    const onLinkClick = () => {
        bigQuery.click(
            {
                type: 'clicked_on_customer_protection',
                buyer: { id: userId },
                component: { name: 'customer_protection_banner' },
                uid: userGuid,
                platform: isTouch ? PLATFORM.MOBILE_WEB : PLATFORM.WEB,
                visit: { id: userGuid },
                out_of_session: false,
            },
            seller ? { seller } : {}
        );
    };

    useEnteredView(observableRef, {
        threshold: 0.9,
        onEntered: () => {
            setPlayTriggered(true);
            bigQuery.pageView(
                {
                    type: 'customer_protection_banner_is_shown',
                    buyer: { id: userId },
                    uid: userGuid,
                    platform: isTouch ? PLATFORM.MOBILE_WEB : PLATFORM.WEB,
                    visit: { id: userGuid },
                    out_of_session: false,
                },
                seller ? { seller } : {}
            );
        },
    });

    useEffect(() => {
        // if the event fired before the handler was attached
        if (!videoReady && (videoRef.current?.readyState || 0) >= 3) {
            // 3 is HAVE_FUTURE_DATA readyState
            setVideoReady(true);
        }
        if (playTriggered && videoReady) {
            playVideo();
        }
    }, [playTriggered, videoReady]);

    const showVideo = isWebmSupported && videoReady;

    return (
        <Stack
            hidden={{ default: true, md: false }}
            ref={observableRef}
            gap="3"
            padding="4"
            alignItems="flexStart"
            borderRadius="sm"
            backgroundColor="grey_200"
            {...props}
        >
            <Container marginTop="1" hidden={showVideo}>
                <GuaranteeIcon size={24} color={theme.colors.grey_1200} />
            </Container>
            <Container hidden={!showVideo} style={{ marginTop: 1 }}>
                <video
                    ref={videoRef}
                    controls={false}
                    onCanPlayThrough={() => setVideoReady(true)}
                    onEnded={onVideoEnd}
                    preload="auto"
                    width={24}
                    height={29}
                    muted
                    playsInline
                >
                    <source src={VIDEO_SRC} type="video/webm" />
                </video>
            </Container>
            <Stack direction="column" gap="0.5">
                <Text size="b_sm" fontWeight="bold">
                    {translate(`${COPY_KEY}.title`)}
                </Text>
                <Container>
                    <Text size="b_sm">
                        {translate(`${COPY_KEY}.content`)}
                        &nbsp;
                        <Link href={HREF} target="_blank" onClick={onLinkClick}>
                            {translate(`${COPY_KEY}.link`)}
                        </Link>
                    </Text>
                </Container>
            </Stack>
        </Stack>
    );
};
